import { useTranslation } from 'react-i18next';
import { usePermissionCatalogue } from './api';

interface Props {
  selected: Set<string>;
  onToggle: (code: string, checked: boolean) => void;
  disabled?: boolean;
}

/**
 * Permission picker grouped area-first (`PermissionArea`), then `Module` then
 * `Action`, populated from the `GET /api/v1/franchise-administration/permissions` catalogue (ADR 0032).
 * Human-readable labels resolve from the frontend translation files keyed by
 * permission code (PRD #15, user story 23); unknown codes fall back to the raw
 * code so a backend-added permission still renders.
 */
export function PermissionPicker({ selected, onToggle, disabled }: Props) {
  const { t } = useTranslation('common');
  const { data, isLoading, isError } = usePermissionCatalogue();

  const labels = t('roles.permissionLabels', { returnObjects: true }) as Record<string, string>;
  const moduleLabels = t('roles.modules', { returnObjects: true }) as Record<string, string>;
  const areaLabels = t('roles.areas', { returnObjects: true }) as Record<string, string>;

  if (isLoading) return <p className="text-muted-foreground">{t('roles.permissions.loading')}</p>;
  if (isError || !data)
    return <p className="text-destructive">{t('roles.permissions.loadFailed')}</p>;

  return (
    <div className="grid gap-6">
      {data.areas.map((area) => (
        <section key={area.area} className="grid gap-3">
          <h3 className="text-sm font-semibold">{areaLabels?.[area.area] ?? area.area}</h3>
          <div className="grid gap-4">
            {area.modules.map((module) => (
              <fieldset key={module.module} className="grid gap-2 rounded-md border p-3">
                <legend className="px-1 text-sm font-medium">
                  {moduleLabels?.[module.module] ?? module.module}
                </legend>
                <div className="grid gap-1.5">
                  {module.actions.flatMap((action) =>
                    action.permissions.map((permission) => (
                      <label key={permission.code} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selected.has(permission.code)}
                          disabled={disabled}
                          onChange={(event) => onToggle(permission.code, event.target.checked)}
                        />
                        <span>{labels?.[permission.code] ?? permission.code}</span>
                      </label>
                    )),
                  )}
                </div>
              </fieldset>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
